import ButtonT from '@components/tailwind-components/Button/ButtonT';
import CheckboxInputT from '@components/tailwind-components/Input/CheckboxInputT';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import SelectInputWithLabelT from '@components/tailwind-components/Input/SelectInputWithLabelT';
import type { TBlockConfig } from '@conversed/shared/src/central/types/block.type';
import { getBlocksWithNewIdAndSubFlow } from '@helpers/flowHelper';
import { toastSuccessTranslated } from '@helpers/toast';
import { useSubflowTemplateInit } from '@hooks/subflow-templates/useSubflowTemplateInit';
import { useSubflowTemplates } from '@hooks/subflow-templates/useSubflowTemplates';
import { useMainSelector } from '@hooks/useMainSelector';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { generate } from 'short-uuid';
import { Actions } from '../../../../constants/actionTypes';
import { useFlowActions } from '../../../../hooks/flow/flow/useFlowActions';
import { useModal } from '../../../../hooks/modal/useModal';
import { useMainDispatch } from '../../../../hooks/useMainDispatch';

interface ModalDataProps {
  name: string;
  setAsMainTab: boolean;
  templateId: string;
  revisionId?: string;
}

export const CreateSubFlowForm = () => {
  const { addSubFlow, setSelectedSubFlow, updateBlocks, deleteBlocks } =
    useFlowActions();
  const { hideModal } = useModal();
  const { t } = useTranslation();

  useSubflowTemplateInit();

  const { subflowTemplates } = useSubflowTemplates();
  const {
    designer: {
      revisions,
      mainRevisionDetail,
      flow: { subFlows },
      blocks,
    },
  } = useMainSelector();
  const dispatch = useMainDispatch();
  const { handleSubmit, control, watch, setValue } = useForm<ModalDataProps>({
    defaultValues: {
      name: '',
      setAsMainTab: false,
      templateId: '',
      revisionId: mainRevisionDetail?._id,
    },
    shouldUseNativeValidation: true,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const setAsMainTab = watch('setAsMainTab');

  useEffect(() => {
    if (setAsMainTab) {
      setValue('name', 'main');
    } else {
      setValue('name', '');
    }
  }, [setAsMainTab, setValue]);

  const hideAndSubmit = useCallback(
    (data: ModalDataProps) => {
      const { name, templateId, revisionId, setAsMainTab } = data;

      const subFlowExists = subFlows.find((subFlow) => subFlow === name);
      // * Check if entered subflow name is already exists or not.
      // * If setAsMainTab is true, then we should not check for subflow name, because we need to allow overriding the main tab.
      if (subFlowExists && !setAsMainTab) {
        setErrorMessage(t('flow.sub-flow.name.exists'));
        return;
      }

      setErrorMessage(null);

      let newSubflowBlocks: TBlockConfig[] = [];
      if (!templateId) {
        newSubflowBlocks = [
          {
            id: generate(),
            type: 'entry' as const,
            subFlow: name,
            revisionId,
          },
        ];
      } else {
        const selectedTemplate = subflowTemplates.find(
          (template) => template._id.toString() === templateId,
        );

        if (selectedTemplate) {
          newSubflowBlocks = getBlocksWithNewIdAndSubFlow(
            selectedTemplate.blocks,
            name,
            revisionId,
          );
        }
      }

      if (setAsMainTab) {
        // * Delete all blocks from the current main subflow if the user wants to set the new subflow as the main tab.
        const defaultRevisionId = revisions?.find(
          (revision) => revision.isMainRevision,
        )?._id;

        // * Get all blocks from the main subflow by revisionId, if the revisionId is not provided, then this means the blocks are in the main revision.
        // * In case of the old flows that do not have revisionId, so that's why we need to say that if the revisionId is not provided, then they are in the default revision.
        const mainSubflowBlockIds =
          (blocks || [])
            .filter(
              (block) =>
                (block.subFlow === 'main' && block.revisionId === revisionId) ||
                (!block.revisionId &&
                  defaultRevisionId &&
                  revisionId === defaultRevisionId),
            )
            ?.map((block) => block.id) || [];

        deleteBlocks(mainSubflowBlockIds);
      }

      if (mainRevisionDetail?._id === revisionId) {
        // * Add subflow tab, only if we are on the main revision
        addSubFlow(name);
        // * Set subflow as active
        setSelectedSubFlow(name);
      }

      // * Add blocks to the subflow
      updateBlocks(newSubflowBlocks);

      dispatch({
        type: Actions.SET_CREATED_NEW_DESIGNER_SUBFLOW,
        payload: name,
      });
      hideModal();
      toastSuccessTranslated('flow.sub-flow.create.toast.success');
    },
    [
      dispatch,
      addSubFlow,
      updateBlocks,
      deleteBlocks,
      setSelectedSubFlow,
      hideModal,
      subflowTemplates,
      subFlows,
      blocks,
      t,
      revisions,
      mainRevisionDetail?._id,
    ],
  );

  return (
    <form
      className='tailwind-modal-form'
      onSubmit={handleSubmit(hideAndSubmit)}>
      <div className='tailwind-modal-body'>
        <p className='text-sm mb-2 italic'>
          {t('flow.sub-flow.add.description')}
        </p>
        <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
          <div className='col-span-1 relative'>
            {errorMessage && (
              <p className='text-red-base text-tiny absolute bottom-[-11px] left-0'>
                {errorMessage}
              </p>
            )}

            <InputWithLabelT
              type='text'
              field='name'
              placeholder={t('flow.sub-flow.name.field')}
              label={t('flow.sub-flow.name.field')}
              id='name'
              control={control}
              inputClassName={errorMessage ? '!ring-red-base' : ''}
              options={{
                required: t('flow.sub-flow.name.required'),
                disabled: setAsMainTab,
                onChange: () => {
                  if (errorMessage) {
                    setErrorMessage(null);
                  }
                },
              }}
            />
          </div>
          <div className='col-span-1'>
            <CheckboxInputT
              id='set-as-main-tab'
              label={t('flow.sub-flow.main-tab.field')}
              control={control}
              field='setAsMainTab'
              className='!gap-1 !mt-0 !mb-3 !mt-2'
              labelClassName='font-medium'
            />
          </div>
          <div className='col-span-1'>
            <SelectInputWithLabelT
              field='templateId'
              label={t('flow.sub-flow.template.field')}
              control={control}
              noOptionSelectedText={t('subflow-templates.no-selected')}
              values={subflowTemplates.map((template) => ({
                value: template._id.toString(),
                description: template.name,
              }))}
            />
          </div>
          <div className='col-span-1'>
            <SelectInputWithLabelT
              field='revisionId'
              label={t('flow.sub-flow.revision.field')}
              control={control}
              values={(revisions || []).map((revision) => ({
                value: revision._id.toString(),
                description: revision.name,
              }))}
            />
          </div>
        </div>
      </div>
      <div className='tailwind-modal-footer'>
        <ButtonT type='submit' styling='primary' size='xs' rounding='md'>
          {t('general.buttons.save')}
        </ButtonT>
      </div>
    </form>
  );
};
